import { useEffect, lazy } from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "react-hot-toast";
import { I18nextProvider } from "react-i18next";
import i18n from "./lib/i18n";

// Components - Keep layout components as regular imports since they're used frequently
import Header from "./components/layout/Header";
import Footer from "./components/layout/Footer";
import LazyWrapper from "./components/ui/LazyWrapper";
import PageTransition from "./components/ui/PageTransition";
import BackToTop from "./components/ui/BackToTop";
import ScrollToTop from "./components/ui/ScrollToTop";
import ServicesPage from "./pages/ServicesPage"; // Regular import to avoid double loading

// Lazy load pages for better code splitting
const HomePage = lazy(() => import("./pages/HomePage"));
const ServiceDetailPage = lazy(() => import("./pages/ServiceDetailPage"));
const SDGPage = lazy(() => import("./pages/SDGPage"));
const EQSPage = lazy(() => import("./pages/EQSPage"));
const PartnersPage = lazy(() => import("./pages/PartnersPage"));
const MarketplacePage = lazy(() => import("./pages/MarketplacePage"));
const MarketplaceDetailPage = lazy(() => import("./pages/MarketplaceDetailPage"));
const AboutPage = lazy(() => import("./pages/AboutPage"));
const ContactPage = lazy(() => import("./pages/ContactPage"));
const ContactProfile = lazy(() => import("./pages/ContactProfile"));
const CartPage = lazy(() => import("./pages/CartPage"));
const OrderSuccessPage = lazy(() => import("./pages/OrderSuccessPage"));
const AuthPage = lazy(() => import("./pages/AuthPage"));

// Lazy load customer components
const CustomerRoute = lazy(() => import("./components/customer/CustomerRoute"));
const CustomerDashboard = lazy(() => import("./pages/CustomerDashboard"));
const CustomerOrders = lazy(() => import("./pages/CustomerOrders"));
const CustomerServiceInquiries = lazy(
  () => import("./pages/CustomerServiceInquiries"),
);
const CustomerProfile = lazy(() => import("./pages/CustomerProfile"));

// Lazy load admin components
const AdminLogin = lazy(() => import("./components/admin/AdminLogin"));
const AdminRoute = lazy(() => import("./components/admin/AdminRoute"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const AdminOrders = lazy(() => import("./pages/AdminOrders"));
const AdminUsers = lazy(() => import("./pages/AdminUsers"));
const AdminServices = lazy(() => import("./pages/AdminServices"));
const AdminServiceCatalog = lazy(() => import("./pages/AdminServiceCatalog"));
const AdminEquipment = lazy(() => import("./pages/AdminEquipment"));
const AdminSuppliers = lazy(() => import("./pages/AdminSuppliers"));
const AdminCodes = lazy(() => import("./pages/AdminCodes"));
const AdminContacts = lazy(() => import("./pages/AdminContacts"));
const AdminLoginLogs = lazy(() => import("./pages/AdminLoginLogs"));
const AdminEmailLogs = lazy(() => import("./pages/AdminEmailLogs"));
const AdminAnnouncements = lazy(() => import("./pages/AdminAnnouncements"));
const AdminEvents = lazy(() => import("./pages/AdminEvents"));
const AdminProjects = lazy(() => import("./pages/AdminProjects"));
const AdminOrderSteps = lazy(() => import("./pages/AdminOrderSteps"));
const AdminSupplierRequests = lazy(() => import("./pages/AdminSupplierRequests"));
const AdminProductRequests = lazy(() => import("./pages/AdminProductRequests"));

// Public announcements page
const AnnouncementsPage = lazy(() => import("./pages/AnnouncementsPage"));

// Public events page
const EventsPage = lazy(() => import("./pages/EventsPage"));

// Public projects page
const ProjectsPage = lazy(() => import("./pages/Projects"));

// Public terms and conditions page
const TermsPage = lazy(() => import("./pages/TermsPage"));

// Lazy load supplier portal components
const SupplierRoute = lazy(() => import("./components/supplier/SupplierRoute"));
const SupplierLogin = lazy(() => import("./pages/SupplierLogin"));
const SupplierRegister = lazy(() => import("./pages/SupplierRegister"));
const SupplierDashboard = lazy(() => import("./pages/SupplierDashboard"));
const SupplierProfile = lazy(() => import("./pages/SupplierProfile"));
const SupplierProducts = lazy(() => import("./pages/SupplierProducts"));
const SupplierProductForm = lazy(() => import("./pages/SupplierProductForm"));

// Store
import { useLanguageStore } from "./store";

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 2,
      refetchOnWindowFocus: false,
    },
  },
});

function App() {
  const { language } = useLanguageStore();

  // Set initial document direction based on language
  useEffect(() => {
    if (language === "ar") {
      document.documentElement.dir = "rtl";
      document.documentElement.lang = "ar";
    } else {
      document.documentElement.dir = "ltr";
      document.documentElement.lang = "en";
    }
    i18n.changeLanguage(language);
  }, [language]);

  return (
    <QueryClientProvider client={queryClient}>
      <I18nextProvider i18n={i18n}>
        <Router
          future={{ v7_startTransition: true, v7_relativeSplatPath: true }}
        >
          <ScrollToTop />
          <Routes>
            {/* Public Routes */}
            <Route
              path="/"
              element={
                <PageTransition className="min-h-screen flex flex-col bg-background text-foreground">
                  <Header />
                  <main className="flex-1">
                    <LazyWrapper>
                      <HomePage />
                    </LazyWrapper>
                  </main>
                  <Footer />
                </PageTransition>
              }
            />
            <Route
              path="/services"
              element={
                <PageTransition className="min-h-screen flex flex-col bg-background text-foreground">
                  <Header />
                  <main className="flex-1">
                    <ServicesPage />
                  </main>
                  <Footer />
                </PageTransition>
              }
            />
            <Route
              path="/services/:id"
              element={
                <PageTransition className="min-h-screen flex flex-col bg-background text-foreground">
                  <Header />
                  <main className="flex-1">
                    <LazyWrapper>
                      <ServiceDetailPage />
                    </LazyWrapper>
                  </main>
                  <Footer />
                </PageTransition>
              }
            />
            <Route
              path="/sdg"
              element={
                <PageTransition className="min-h-screen flex flex-col bg-background text-foreground">
                  <Header />
                  <main className="flex-1">
                    <LazyWrapper>
                      <SDGPage />
                    </LazyWrapper>
                  </main>
                  <Footer />
                </PageTransition>
              }
            />
            <Route
              path="/eqs"
              element={
                <PageTransition className="min-h-screen flex flex-col bg-background text-foreground">
                  <Header />
                  <main className="flex-1">
                    <LazyWrapper>
                      <EQSPage />
                    </LazyWrapper>
                  </main>
                  <Footer />
                </PageTransition>
              }
            />
            <Route
              path="/partners"
              element={
                <PageTransition className="min-h-screen flex flex-col bg-background text-foreground">
                  <Header />
                  <main className="flex-1">
                    <LazyWrapper>
                      <PartnersPage />
                    </LazyWrapper>
                  </main>
                  <Footer />
                </PageTransition>
              }
            />
            <Route
              path="/terms"
              element={
                <PageTransition className="min-h-screen flex flex-col bg-background text-foreground">
                  <Header />
                  <main className="flex-1">
                    <LazyWrapper>
                      <TermsPage />
                    </LazyWrapper>
                  </main>
                  <Footer />
                </PageTransition>
              }
            />
            <Route
              path="/marketplace"
              element={
                <PageTransition className="min-h-screen flex flex-col bg-background text-foreground">
                  <Header />
                  <main className="flex-1">
                    <LazyWrapper>
                      <MarketplacePage />
                    </LazyWrapper>
                  </main>
                  <Footer />
                </PageTransition>
              }
            />
            <Route
              path="/marketplace/:id"
              element={
                <PageTransition className="min-h-screen flex flex-col bg-background text-foreground">
                  <Header />
                  <main className="flex-1">
                    <LazyWrapper>
                      <MarketplaceDetailPage />
                    </LazyWrapper>
                  </main>
                  <Footer />
                </PageTransition>
              }
            />
            <Route
              path="/cart"
              element={
                <PageTransition className="min-h-screen flex flex-col bg-background text-foreground">
                  <Header />
                  <main className="flex-1">
                    <LazyWrapper>
                      <CartPage />
                    </LazyWrapper>
                  </main>
                  <Footer />
                </PageTransition>
              }
            />
            <Route
              path="/order-success"
              element={
                <PageTransition className="min-h-screen flex flex-col bg-background text-foreground">
                  <Header />
                  <main className="flex-1">
                    <LazyWrapper>
                      <OrderSuccessPage />
                    </LazyWrapper>
                  </main>
                  <Footer />
                </PageTransition>
              }
            />
            <Route
              path="/about"
              element={
                <PageTransition className="min-h-screen flex flex-col bg-background text-foreground">
                  <Header />
                  <main className="flex-1">
                    <LazyWrapper>
                      <AboutPage />
                    </LazyWrapper>
                  </main>
                  <Footer />
                </PageTransition>
              }
            />
            <Route
              path="/contact"
              element={
                <PageTransition className="min-h-screen flex flex-col bg-background text-foreground">
                  <Header />
                  <main className="flex-1">
                    <LazyWrapper>
                      <ContactPage />
                    </LazyWrapper>
                  </main>
                  <Footer />
                </PageTransition>
              }
            />
            <Route
              path="/contact-card"
              element={
                <PageTransition className="min-h-screen bg-background text-foreground">
                  <LazyWrapper>
                    <ContactProfile />
                  </LazyWrapper>
                </PageTransition>
              }
            />
            <Route
              path="/announcements"
              element={
                <PageTransition className="min-h-screen flex flex-col bg-background text-foreground">
                  <Header />
                  <main className="flex-1">
                    <LazyWrapper>
                      <AnnouncementsPage />
                    </LazyWrapper>
                  </main>
                  <Footer />
                </PageTransition>
              }
            />
            <Route
              path="/events"
              element={
                <PageTransition className="min-h-screen flex flex-col bg-background text-foreground">
                  <Header />
                  <main className="flex-1">
                    <LazyWrapper>
                      <EventsPage />
                    </LazyWrapper>
                  </main>
                  <Footer />
                </PageTransition>
              }
            />
            <Route
              path="/projects"
              element={
                <PageTransition className="min-h-screen flex flex-col bg-background text-foreground">
                  <Header />
                  <main className="flex-1">
                    <LazyWrapper>
                      <ProjectsPage />
                    </LazyWrapper>
                  </main>
                  <Footer />
                </PageTransition>
              }
            />

            {/* Auth Routes */}
            <Route
              path="/login"
              element={
                <PageTransition>
                  <LazyWrapper>
                    <AuthPage />
                  </LazyWrapper>
                </PageTransition>
              }
            />

            {/* Customer Dashboard Routes */}
            <Route
              path="/dashboard"
              element={
                <PageTransition>
                  <LazyWrapper>
                    <CustomerRoute>
                      <CustomerDashboard />
                    </CustomerRoute>
                  </LazyWrapper>
                </PageTransition>
              }
            />
            <Route
              path="/dashboard/orders"
              element={
                <PageTransition>
                  <LazyWrapper>
                    <CustomerRoute>
                      <CustomerOrders />
                    </CustomerRoute>
                  </LazyWrapper>
                </PageTransition>
              }
            />
            <Route
              path="/dashboard/service-inquiries"
              element={
                <PageTransition>
                  <LazyWrapper>
                    <CustomerRoute>
                      <CustomerServiceInquiries />
                    </CustomerRoute>
                  </LazyWrapper>
                </PageTransition>
              }
            />
            <Route
              path="/dashboard/profile"
              element={
                <PageTransition>
                  <LazyWrapper>
                    <CustomerRoute>
                      <CustomerProfile />
                    </CustomerRoute>
                  </LazyWrapper>
                </PageTransition>
              }
            />

            {/* Admin Routes */}
            <Route
              path="/admin/login"
              element={
                <PageTransition>
                  <LazyWrapper>
                    <AdminLogin />
                  </LazyWrapper>
                </PageTransition>
              }
            />
            <Route
              path="/admin"
              element={
                <PageTransition>
                  <LazyWrapper>
                    <AdminRoute>
                      <AdminDashboard />
                    </AdminRoute>
                  </LazyWrapper>
                </PageTransition>
              }
            />
            <Route
              path="/admin/orders"
              element={
                <PageTransition>
                  <LazyWrapper>
                    <AdminRoute>
                      <AdminOrders />
                    </AdminRoute>
                  </LazyWrapper>
                </PageTransition>
              }
            />
            <Route
              path="/admin/order-steps"
              element={
                <PageTransition>
                  <LazyWrapper>
                    <AdminRoute>
                      <AdminOrderSteps />
                    </AdminRoute>
                  </LazyWrapper>
                </PageTransition>
              }
            />
            <Route
              path="/admin/users"
              element={
                <PageTransition>
                  <LazyWrapper>
                    <AdminRoute>
                      <AdminUsers />
                    </AdminRoute>
                  </LazyWrapper>
                </PageTransition>
              }
            />
            <Route
              path="/admin/services"
              element={
                <PageTransition>
                  <LazyWrapper>
                    <AdminRoute>
                      <AdminServices />
                    </AdminRoute>
                  </LazyWrapper>
                </PageTransition>
              }
            />
            <Route
              path="/admin/service-catalog"
              element={
                <PageTransition>
                  <LazyWrapper>
                    <AdminRoute>
                      <AdminServiceCatalog />
                    </AdminRoute>
                  </LazyWrapper>
                </PageTransition>
              }
            />
            <Route
              path="/admin/equipment"
              element={
                <PageTransition>
                  <LazyWrapper>
                    <AdminRoute>
                      <AdminEquipment />
                    </AdminRoute>
                  </LazyWrapper>
                </PageTransition>
              }
            />
            <Route
              path="/admin/suppliers"
              element={
                <PageTransition>
                  <LazyWrapper>
                    <AdminRoute>
                      <AdminSuppliers />
                    </AdminRoute>
                  </LazyWrapper>
                </PageTransition>
              }
            />
            <Route
              path="/admin/supplier-requests"
              element={
                <PageTransition>
                  <LazyWrapper>
                    <AdminRoute>
                      <AdminSupplierRequests />
                    </AdminRoute>
                  </LazyWrapper>
                </PageTransition>
              }
            />
            <Route
              path="/admin/product-requests"
              element={
                <PageTransition>
                  <LazyWrapper>
                    <AdminRoute>
                      <AdminProductRequests />
                    </AdminRoute>
                  </LazyWrapper>
                </PageTransition>
              }
            />
            <Route
              path="/admin/codes"
              element={
                <PageTransition>
                  <LazyWrapper>
                    <AdminRoute>
                      <AdminCodes />
                    </AdminRoute>
                  </LazyWrapper>
                </PageTransition>
              }
            />
            <Route
              path="/admin/contacts"
              element={
                <PageTransition>
                  <LazyWrapper>
                    <AdminRoute>
                      <AdminContacts />
                    </AdminRoute>
                  </LazyWrapper>
                </PageTransition>
              }
            />
            <Route
              path="/admin/login-logs"
              element={
                <PageTransition>
                  <LazyWrapper>
                    <AdminRoute>
                      <AdminLoginLogs />
                    </AdminRoute>
                  </LazyWrapper>
                </PageTransition>
              }
            />
            <Route
              path="/admin/email-logs"
              element={
                <PageTransition>
                  <LazyWrapper>
                    <AdminRoute>
                      <AdminEmailLogs />
                    </AdminRoute>
                  </LazyWrapper>
                </PageTransition>
              }
            />
            <Route
              path="/admin/events"
              element={
                <PageTransition>
                  <LazyWrapper>
                    <AdminRoute>
                      <AdminEvents />
                    </AdminRoute>
                  </LazyWrapper>
                </PageTransition>
              }
            />
            <Route
              path="/admin/projects"
              element={
                <PageTransition>
                  <LazyWrapper>
                    <AdminRoute>
                      <AdminProjects />
                    </AdminRoute>
                  </LazyWrapper>
                </PageTransition>
              }
            />
            <Route
              path="/admin/announcements"
              element={
                <PageTransition>
                  <LazyWrapper>
                    <AdminRoute>
                      <AdminAnnouncements />
                    </AdminRoute>
                  </LazyWrapper>
                </PageTransition>
              }
            />

            {/* Supplier Portal Routes */}
            <Route path="/supplier/register" element={
              <PageTransition><LazyWrapper><SupplierRegister /></LazyWrapper></PageTransition>
            } />
            <Route path="/supplier/login" element={
              <PageTransition><LazyWrapper><SupplierLogin /></LazyWrapper></PageTransition>
            } />
            <Route path="/supplier" element={
              <PageTransition><LazyWrapper><SupplierRoute><SupplierDashboard /></SupplierRoute></LazyWrapper></PageTransition>
            } />
            <Route path="/supplier/profile" element={
              <PageTransition><LazyWrapper><SupplierRoute><SupplierProfile /></SupplierRoute></LazyWrapper></PageTransition>
            } />
            <Route path="/supplier/products" element={
              <PageTransition><LazyWrapper><SupplierRoute><SupplierProducts /></SupplierRoute></LazyWrapper></PageTransition>
            } />
            <Route path="/supplier/products/new" element={
              <PageTransition><LazyWrapper><SupplierRoute><SupplierProductForm /></SupplierRoute></LazyWrapper></PageTransition>
            } />
            <Route path="/supplier/products/:id/edit" element={
              <PageTransition><LazyWrapper><SupplierRoute><SupplierProductForm /></SupplierRoute></LazyWrapper></PageTransition>
            } />
          </Routes>
          <BackToTop />
          {/* <ChatButton />
          <ChatModal /> */}
          <Toaster
            position="top-right"
            toastOptions={{
              duration: 4000,
              style: {
                background: "hsl(var(--background))",
                color: "hsl(var(--foreground))",
                border: "1px solid hsl(var(--border))",
              },
            }}
          />
        </Router>
      </I18nextProvider>
    </QueryClientProvider>
  );
}

export default App;
